/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  POST_ACESDBS_FILES,
  GET_ACESDBS_FILES_BY_FILE_ID,
  DELETE_ACESDBS_FILES_BY_FILE_ID,
} from './actionTypes';

import {
  postAcesdbsFilesUtil,
  getAcesdbsFilesByFileIdUtil,
  deleteAcesdbsFilesByFileIdUtil,
} from './utils';

/**
 * Contains the action creator functions that update the ACESDocumentService slice of the store.
 *
 * @namespace ACESDocumentServiceActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/acesdbs/files
   *
   * @param {NewSPMFileUploadContents} data object that will be posted to the api
   * @param {function} callback function to call once this function has finished
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static postAcesdbsFiles = (dispatch, newSPMFileUploadContents, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      postAcesdbsFilesUtil.bind(null, newSPMFileUploadContents),
      POST_ACESDBS_FILES,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to postAcesdbsFiles.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static resetPostAcesdbsFiles = (dispatch, data) => {
    const json = {
      type: POST_ACESDBS_FILES.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to postAcesdbsFiles.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static resetPostAcesdbsFilesError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, POST_ACESDBS_FILES.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/acesdbs/files/{fileId}
   *
   * @param {string} fileId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static getAcesdbsFilesByFileId = (dispatch, fileId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getAcesdbsFilesByFileIdUtil.bind(null, fileId),

      GET_ACESDBS_FILES_BY_FILE_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getAcesdbsFilesByFileId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static resetGetAcesdbsFilesByFileId = (dispatch, data) => {
    const json = {
      type: GET_ACESDBS_FILES_BY_FILE_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getAcesdbsFilesByFileId.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static resetGetAcesdbsFilesByFileIdError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_ACESDBS_FILES_BY_FILE_ID.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/acesdbs/files/{fileId}
   *
   * @param {string} fileId.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static deleteAcesdbsFilesByFileId = (dispatch, fileId, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      deleteAcesdbsFilesByFileIdUtil.bind(null, fileId),
      DELETE_ACESDBS_FILES_BY_FILE_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to deleteAcesdbsFilesByFileId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static resetDeleteAcesdbsFilesByFileId = (dispatch, data) => {
    const json = {
      type: DELETE_ACESDBS_FILES_BY_FILE_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to deleteAcesdbsFilesByFileId.
   *
   * @param {function} dispatch
   * @function
   * @memberof ACESDocumentServiceActions
   */
  static resetDeleteAcesdbsFilesByFileIdError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, DELETE_ACESDBS_FILES_BY_FILE_ID.failureAction);
  };
}
